import { css, html, LitElement } from 'lit';

import { customElement, property } from 'lit/decorators.js';
import '../fields/d-expansion.js';
import '../fields/d-closer.js';
import '../fields/d-view-info.js';
import { LocalDate } from 'src/utilities/local-date';
import { FileViewerDialog } from 'src/layout/parts/file-viewer-dialog';
import { SignableDocumentDialog } from 'src/layout/parts/signable-document-dialog';
import { RequestSignaturesDialog } from 'src/layout/parts/request-signatures-dialog';
import { isEmptyHtmlContent, joinWithAnd } from 'src/utilities/text';
import { sortBy } from 'lodash';
import '../../library/lists/d-list-section-signing-orders';
import type { SigningOrderListItem } from '../lists/signing-order-list-item';
import type { SignableDocumentDialogResult } from '../../layout/parts/signable-document-dialog-result';
import type { SignableEntity } from './signable-entity';
import type { FileViewerInput } from '../../layout/parts/file-viewer-input';
import type { RequestSignaturesInput } from '../../layout/parts/request-signatures-input';
import type { RequestSignaturesResult } from '../../layout/parts/request-signatures-result';
import type { SignableDocumentDialogInput } from '../../layout/parts/signable-document-dialog-input';
import { assertIsDefined } from '../../assertIsDefined';
import type { SigningOrderWithAsDocument } from 'src/library/components/signing-order-with-as-document';
import { t } from 'src/i18n';

/**
 *
 * The help section closes whenever the content changes, ie in connection with navigating to a different page.
 *
 * STATUS OK
 */
@customElement('d-signatures-section')
export class DSignaturesSection extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
  `;

  @property({ type: Object })
  entityView!: SignableEntity;
  @property({ type: Boolean })
  signingDisabled = false;

  private get signingOrdersListItems(): SigningOrderListItem[] {
    return this.entityView.signingOrders.map((s) => {
      const signedBy = sortBy(
        (s.signatories || [])
          .filter((e) => e.signatoryStatus === 'SIGNED')
          .map((e) => {
            const employee = this.entityView.availableEmployees.find((v) => v.uuid === e.uuid);
            if (employee) {
              return employee.firstName + ' ' + employee.lastName;
            }
            const contact = this.entityView.availablePartners.flatMap((x) => x.contacts).find((v) => v.uuid === e.uuid);
            if (contact) {
              const p = this.entityView.availablePartners.find((x) => x.uuid === contact.partnerUuid);
              console.log(contact, this.entityView.availablePartners);
              return contact.firstName + ' ' + contact.lastName + ' (' + p?.name + ')';
            }
            const contact2 = this.entityView.availableContacts.find((v) => v.uuid === e.uuid);
            if (contact2) {
              return contact2.firstName + ' ' + contact2.lastName;
            }
            return '-';
          }),
      );
      const waitingFor = sortBy(
        (s.signatories || [])
          .filter((e) => e.signatoryStatus !== 'SIGNED')
          .map((e) => {
            const employee = this.entityView.availableEmployees.find((v) => v.uuid === e.uuid);
            if (employee) {
              return employee.firstName + ' ' + employee.lastName;
            }
            const contact = this.entityView.availablePartners.flatMap((x) => x.contacts).find((v) => v.uuid === e.uuid);
            if (contact) {
              const p = this.entityView.availablePartners.find((x) => x.uuid === contact.partnerUuid);
              console.log(contact, this.entityView.availablePartners);
              return contact.firstName + ' ' + contact.lastName + ' (' + p?.name + ')';
            }
            const contact2 = this.entityView.availableContacts.find((v) => v.uuid === e.uuid);
            if (contact2) {
              return contact2.firstName + ' ' + contact2.lastName;
            }
            return '-';
          }),
      );
      const l: string[] = [];
      if (signedBy.length > 0) {
        l.push(t('library.signatures.signedBy', { names: joinWithAnd(signedBy) }));
      }
      if (waitingFor.length > 0) {
        let text = t('library.signatures.waitingFor', { names: joinWithAnd(waitingFor) });
        if (s.signingOrderStatus === 'CANCELLED') {
          text = t('library.signatures.notSignedBy', { names: joinWithAnd(waitingFor) });
        }
        l.push(text);
      }
      let status: string = '';
      if (s.signingOrderStatus === 'OPEN') {
        status = t('library.signatures.created', { date: LocalDate.fromString(s.createdAt).toStringForDisplay() });
      }
      if (s.signingOrderStatus === 'CLOSED') {
        status = t('library.signatures.signed', { date: LocalDate.fromString(s.closedAt).toStringForDisplay() });
      }
      if (s.signingOrderStatus === 'CANCELLED') {
        status = t('library.signatures.cancelled', { date: LocalDate.fromString(s.closedAt).toStringForDisplay() });
      }
      if (s.signingOrderStatus === 'EXPIRED') {
        status = t('library.signatures.expired', { date: LocalDate.fromString(s.closedAt).toStringForDisplay() });
      }
      return {
        label: status,
        secondaryLabel: l.join('; '),
        status: s.signingOrderStatus,
        asDocumentItem: s.asDocumentItem,
        uuid: s.uuid,
      };
    });
  }

  async onSigningItemClick(item: SigningOrderListItem) {
    if (item.status === 'XXCLOSED') {
      console.log('onSigningItemClick item', item);
      const input: FileViewerInput = {
        document: await item.asDocumentItem(),
        writeAccess: this.entityView.currentUserHasWriteAccess,
        refresh: () => item.asDocumentItem(),
      };
      await FileViewerDialog.open(input);
    } else if (item.status == 'OPEN' || item.status === 'CLOSED') {
      const signingOrder: SigningOrderWithAsDocument | undefined = this.entityView.signingOrders.find(
        (x) => x.uuid === item.uuid,
      );
      assertIsDefined(signingOrder);

      let previewHref = '';
      if (signingOrder.signingOrderStatus === 'CLOSED') {
        const documentItem = await item.asDocumentItem();
        previewHref = documentItem.url;
      }
      const input: SignableDocumentDialogInput = {
        status: item.status,
        requestDate: signingOrder.createdAt,
        requestMessage: signingOrder.message ?? '',
        dueDate: signingOrder.dueDate,
        previewHref: previewHref,
        asDocumentItem: async () => await item.asDocumentItem(),
        signees: signingOrder.signatories.map((s) => {
          const e = this.entityView.availableEmployees.find((x) => x.uuid === s.uuid);
          const p = this.entityView.availablePartners.flatMap((x) => x.contacts).find((x) => x.uuid === s.uuid);
          const p2 = this.entityView.availableContacts.find((x) => x.uuid === s.uuid);

          const personForSigning = e ?? p ?? p2;

          assertIsDefined(personForSigning);

          const partnerName = this.entityView.availablePartners.find(
            (u) => u.uuid === personForSigning.partnerUuid,
          )?.name;

          return {
            status: s.signatoryStatus,
            label: personForSigning.firstName + ' ' + personForSigning.lastName,
            secondaryLabel: partnerName !== undefined ? t('library.signatures.forPartner', { partnerName }) : '',
            rightLabel:
              s.signatoryStatus === 'SIGNED'
                ? t('library.signatures.statusSigned')
                : t('library.signatures.statusWaiting'),
          };
        }),
      };
      const result: SignableDocumentDialogResult = await SignableDocumentDialog.open(input);
      if (result.action === 'cancel-signing') {
        this.dispatchEvent(
          new CustomEvent<{
            entityType: string;
            entityUuid: string;
            signingOrderId: string;
          }>('cancel-signing-order', {
            bubbles: true,
            composed: true,
            detail: {
              entityType: this.entityView.type,
              entityUuid: this.entityView.uuid,
              signingOrderId: signingOrder.signatureOrderId,
            },
          }),
        );
      } else if (result.action === 'extend-signing') {
        this.dispatchEvent(
          new CustomEvent<{
            entityType: string;
            entityUuid: string;
            signingOrderId: string;
            dueDate: string;
          }>('extend-signing-order', {
            bubbles: true,
            composed: true,
            detail: {
              entityType: this.entityView.type,
              entityUuid: this.entityView.uuid,
              signingOrderId: signingOrder.signatureOrderId,
              dueDate: result.dueDate,
            },
          }),
        );
      }
    }
  }

  async onRequestSignatures(e: CustomEvent) {
    e.preventDefault();
    e.stopPropagation();
    const input: RequestSignaturesInput = {
      organizationName: this.entityView.organizationName,
      currentUserIsOwner: this.entityView.currentUserIsOwner,
      activeEmployeesCount: this.entityView.activeEmployeesCount,
      entityTypeName:
        this.entityView.type === 'reports'
          ? t('library.signatures.entityTypeReport')
          : t('library.signatures.entityTypeContract'),
      entityName: this.entityView.name,
      hasTextContent: !isEmptyHtmlContent(this.entityView.content),
      attachments: this.entityView.attachments.map((a) => ({ text: a.name, value: a.uuid })),
      availableSigneesForOrganization: this.entityView.availableEmployees,
      contractPartyPartners: this.entityView.availablePartners
        .filter((p) => this.entityView.partners.includes(p.uuid))
        .map((p) => {
          return p;
        }),
      contractPartyEmployees: this.entityView.availableEmployees.filter((e) =>
        this.entityView.employees.includes(e.uuid),
      ),
      showOrganization: this.entityView.type !== 'reports',
      contractPartyContacts: this.entityView.availableContacts.filter((e) => this.entityView.contacts.includes(e.uuid)),
    };
    const result: RequestSignaturesResult = await RequestSignaturesDialog.open(input);
    if (result.action === 'send') {
      this.dispatchEvent(
        new CustomEvent('request-signatures', {
          bubbles: true,
          composed: true,
          detail: {
            entityType: this.entityView.type,
            entityUuid: this.entityView.uuid,
            content: result.content,
            dueDate: result.dueDate,
            signeesForOrganization: result.signeesForOrganization,
            signeesForPartners: result.signeesForPartners.map((x) => x.uuid),
            signeesAsEmployees: result.signeesAsEmployees,
            message: result.message,
          },
        }),
      );
    }
  }

  render() {
    return html`<d-list-section-signing-orders
      .items=${this.signingOrdersListItems}
      .signingDisabled=${this.signingDisabled}
      @request-signatures=${async (e) => await this.onRequestSignatures(e)}
      @signing-item-click=${async (e) => await this.onSigningItemClick(e.detail.value)}
    ></d-list-section-signing-orders>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-signatures-section': DSignaturesSection;
  }
}
